/**
 * A package line's quantity cell — **Qty rule** (`builder-engine-and-
 * consistency.md` §7.1 item 5, Board 5 callout ④).
 *
 *   [0.3] + [0.00005] / sq ft
 *   = 0.6 h at 6,000   [2 tiers]
 *
 * A base plus a rate per measure, with what the two come to at the sample
 * beneath them, and a tiers chip only on a line that has tiers. Props only:
 * the resolved number is the server's preview, never worked out here. The
 * boxes keep what is being typed ("0.") and hand up a number once it reads as
 * one, so the preview follows the typing without a decimal point vanishing.
 */
import { useEffect, useState, type MouseEvent } from 'react'
import { PER_MEASURE, TIERS_CHIP } from '../copy.js'
import { UnitInput } from './unit-input.js'
import { readNumber } from './tiers.js'

export interface RuleCellProps {
  base: number
  rate: number
  /** What the rate is per — the package's measure or its quantity unit. */
  per: string
  /** "= 1.2 bag at 6,000", from the preview; null while it is being worked out. */
  resolves: string | null
  /** Only on a line that has tiers. */
  tierCount?: number
  onTiers?: (event: MouseEvent<HTMLElement>) => void
  onBase: (value: number) => void
  onRate: (value: number) => void
  readOnly?: boolean
  label: string
}

/** "0.000050" → "0.00005": a field somebody edits, not a figure to read. */
const shown = (value: number) => String(Number(value.toFixed(6)))

export function RuleCell({
  base, rate, per, resolves, tierCount = 0, onTiers, onBase, onRate, readOnly, label,
}: RuleCellProps) {
  const [baseText, setBaseText] = useState(shown(base))
  const [rateText, setRateText] = useState(shown(rate))
  useEffect(() => { if (readNumber(baseText) !== base) setBaseText(shown(base)) }, [base])
  useEffect(() => { if (readNumber(rateText) !== rate) setRateText(shown(rate)) }, [rate])

  const take = (text: string, set: (value: string) => void, push: (value: number) => void) => {
    set(text)
    const value = readNumber(text)
    if (text.trim() !== '' && Number.isFinite(value) && value >= 0) push(value)
  }

  return (
    <span data-rule-cell="" className="flex min-w-0 flex-col gap-[3px] py-0.5">
      <span className="flex items-center gap-1.5">
        <UnitInput
          label={`${label} base`}
          value={baseText}
          onChange={(text) => take(text, setBaseText, onBase)}
          size="small"
          width={52}
          align="right"
          readOnly={readOnly}
        />
        <span aria-hidden className="text-[12px] text-text-faint">+</span>
        <UnitInput
          label={`${label} rate`}
          value={rateText}
          onChange={(text) => take(text, setRateText, onRate)}
          size="small"
          width={86}
          align="right"
          dim={rate === 0}
          readOnly={readOnly}
        />
        <span className="whitespace-nowrap text-[11.5px] text-text-muted">{PER_MEASURE(per)}</span>
      </span>
      <span className="flex min-w-0 items-center gap-1.5">
        {resolves && <span className="tnum truncate text-[11.5px] text-text-faint">{resolves}</span>}
        {tierCount > 1 && (
          <button
            type="button"
            data-tiers-chip=""
            onClick={onTiers}
            className="inline-flex h-[18px] shrink-0 items-center rounded-tag border border-accent-line bg-accent-soft px-1.5 text-[10.5px] font-semibold text-accent-dark"
          >
            {TIERS_CHIP(tierCount)}
          </button>
        )}
      </span>
    </span>
  )
}
